import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthenticationService } from '../../../core/services/authentication.service';
import { ApiService } from '../../../core/services/api.service';

@Component({
  selector: 'app-accept-invitation',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, CommonModule],
  templateUrl: './accept-invitation.component.html'
})
export class AcceptInvitationComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private authService = inject(AuthenticationService);
  private apiService = inject(ApiService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  form: FormGroup;
  isLoading = false;
  isLoadingInvitation = false;
  errorMessage = '';

  // Populated from the invitation-info API
  invitedEmail = '';
  invitedPhone = '';

  constructor() {
    this.form = this.formBuilder.group({
      invitationCode: ['', [Validators.required]],
      accountEmail:   [''],
      accountPhone:   ['', [Validators.required]],
      fullName:       ['', [Validators.required]],
      username:       ['', [Validators.required, Validators.minLength(3)]],
      password:       ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword:['', [Validators.required]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    const code = this.route.snapshot.queryParamMap.get('code');
    if (code) {
      this.form.get('invitationCode')?.setValue(code);
      this.form.get('invitationCode')?.disable();
      this.loadInvitationInfo(code);
    }
  }

  loadInvitationInfo(code: string): void {
    this.isLoadingInvitation = true;
    this.errorMessage = '';
    this.apiService.get<{ email: string; phone: string; expiresAt: string }>(`auth/invitation-info/${code}`)
      .subscribe({
        next: (res: any) => {
          this.isLoadingInvitation = false;
          if (res.success) {
            this.invitedEmail = res.data.email ?? '';
            this.invitedPhone = res.data.phone ?? '';
            // Pre-fill phone if invited by phone
            if (this.invitedPhone) {
              this.form.get('accountPhone')?.setValue(this.invitedPhone);
            }
            // Pre-fill email if invited by email
            if (this.invitedEmail) {
              this.form.get('accountEmail')?.setValue(this.invitedEmail);
            }
          } else {
            this.errorMessage = res.message || 'Invalid or expired invitation.';
          }
        },
        error: () => {
          this.isLoadingInvitation = false;
          this.errorMessage = 'Could not load invitation. Please check your link.';
        }
      });
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password');
    const confirmPassword = control.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach(c => c.markAsTouched());
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const raw = this.form.getRawValue();

    const request: any = {
      invitationCode: raw.invitationCode,
      email:          this.invitedEmail || undefined,
      phone:          this.invitedPhone || undefined,
      accountEmail:   raw.accountEmail || undefined,
      accountPhone:   raw.accountPhone,
      username:       raw.username,
      password:       raw.password,
      fullName:       raw.fullName
    };

    this.authService.acceptInvitation(request).subscribe({
      next: (response: any) => {
        if (response.success) {
          this.router.navigate(['/home']);
        } else {
          this.errorMessage = response.message || 'Failed to accept invitation';
        }
        this.isLoading = false;
      },
      error: (error: any) => {
        this.errorMessage = error.error?.message || 'An error occurred';
        this.isLoading = false;
      }
    });
  }

  get f() { return this.form.controls; }

  get passwordMismatch(): boolean {
    return this.form.hasError('passwordMismatch') && this.f['confirmPassword'].touched;
  }
}
